'use client'

import DynamicFormGenerator from '@/components/organisms/dynamic-form-generator'
import CustomComponentRenderer from '@/components/organisms/custom-component-renderer'
import ArticleRenderer from '@/components/organisms/article-renderer'
import FilePreview from '@/components/molecules/file-preview'
import { useConfig } from '@/context/ConfigProvider'
import { useSocketContext } from '@/context/SocketProvider'
import { hasRenderableArticleContent } from '@/lib/article-content'
import { isInternalFileUploadedMarker, stripInternalFileUploadedMarker } from '@/lib/internal-message-markers'
import { formatTime } from '@/lib/time'
import { cn } from '@/lib/utils'
import { ChatMessage, useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import MarkdownMessage from '@/views/chat/markdown-message'
import { Download, FileText, Headset, TicketIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

interface MessageItemProps {
  message: ChatMessage
  isLastMessage?: boolean
}

interface SaveFilePickerOptions {
  suggestedName?: string
}

interface FileSystemWritableFileStream {
  write(data: Blob): Promise<void>
  close(): Promise<void>
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>
}

declare global {
  interface Window {
    showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>
  }
}

const extractUrls = (content: string) => content.match(/https?:\/\/[^\s<>"')\]]+/g) ?? []

const isFromFileService = (candidate: string, base: string) => {
  try {
    const candidateUrl = new URL(candidate)
    const baseUrl = new URL(base)
    const basePath = baseUrl.pathname.replace(/\/+$/, '')

    return (
      candidateUrl.origin === baseUrl.origin &&
      (basePath === '' || candidateUrl.pathname === basePath || candidateUrl.pathname.startsWith(`${basePath}/`))
    )
  } catch {
    return false
  }
}

const getFilenameFromContentDisposition = (contentDisposition: string | null, fallback: string) => {
  if (!contentDisposition) return fallback

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1])

  const basicMatch = contentDisposition.match(/filename=(?:"([^"]*)"|([^;]*))/i)
  const basicFilename = (basicMatch?.[1] ?? basicMatch?.[2])?.trim()
  if (basicFilename) return basicFilename

  return fallback
}

const getFilenameFromUrl = (src: string) => {
  try {
    const pathname = new URL(src).pathname
    const filename = pathname.split('/').filter(Boolean).pop()

    return filename || 'file'
  } catch {
    return 'file'
  }
}

const OperatorFileAttachment = ({ src }: { src: string }) => {
  const token = usePersistStore(state => state.token)
  const guestId = usePersistStore(state => state.guestId)
  const siteId = usePersistStore(state => state.siteId)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [fileBlob, setFileBlob] = useState<Blob | null>(null)
  const [fileName, setFileName] = useState(() => getFilenameFromUrl(src))
  const [contentType, setContentType] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const buildAuthenticatedRequest = useCallback(() => {
    const url = new URL(src)
    if (siteId && !url.searchParams.get('site_id')) {
      url.searchParams.set('site_id', siteId)
    }

    const downloadToken = token === 'guest' ? guestId : token
    const headers: HeadersInit = {}
    if (downloadToken) {
      headers.Authorization = `Bearer ${downloadToken}`
    }

    return { url, headers }
  }, [src, siteId, token, guestId])

  useEffect(() => {
    let revokedUrl: string | null = null
    const controller = new AbortController()

    async function loadFile() {
      try {
        const { url, headers } = buildAuthenticatedRequest()

        const response = await fetch(url.toString(), {
          headers,
          signal: controller.signal
        })
        if (!response.ok) {
          return
        }

        const responseContentType = response.headers.get('content-type') || 'application/octet-stream'
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        revokedUrl = objectUrl
        setFileBlob(blob)
        setObjectUrl(objectUrl)
        setContentType(responseContentType)
        setFileName(
          getFilenameFromContentDisposition(response.headers.get('content-disposition'), getFilenameFromUrl(src))
        )
      } catch {
        if (!controller.signal.aborted) {
          setFileBlob(null)
          setObjectUrl(null)
          setContentType(null)
        }
      }
    }

    loadFile()

    return () => {
      controller.abort()
      if (revokedUrl) URL.revokeObjectURL(revokedUrl)
    }
  }, [buildAuthenticatedRequest, src])

  const handleDownload = async () => {
    setIsDownloading(true)

    try {
      let blob = fileBlob
      let name = fileName

      if (!blob) {
        const { url, headers } = buildAuthenticatedRequest()
        const response = await fetch(url.toString(), { headers })
        if (!response.ok) return

        blob = await response.blob()
        name = getFilenameFromContentDisposition(response.headers.get('content-disposition'), getFilenameFromUrl(src))
        setFileBlob(blob)
        setFileName(name)
      }

      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({ suggestedName: name })
          const writable = await handle.createWritable()
          await writable.write(blob)
          await writable.close()

          return
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return
        }
      }

      const fallbackUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = fallbackUrl
      link.download = name
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(fallbackUrl)
    } finally {
      setIsDownloading(false)
    }
  }

  if (objectUrl && contentType?.startsWith('image/')) {
    return (
      <a href={objectUrl} download={fileName} className='block overflow-hidden rounded-lg'>
        <img src={objectUrl} alt={fileName} className='max-h-72 max-w-full rounded-lg object-contain' />
      </a>
    )
  }

  return (
    <button
      type='button'
      disabled={isDownloading}
      className='border-border/70 bg-background/80 hover:bg-background disabled:hover:bg-background flex max-w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-80'
      onClick={handleDownload}
    >
      <Download className='size-4 shrink-0' />
      <span className='min-w-0 truncate'>{fileName}</span>
    </button>
  )
}

const MessageItem = forwardRef<HTMLDivElement, MessageItemProps>(({ message, isLastMessage }, ref) => {
  const isContentString = typeof message?.content === 'string'
  const { fileServiceUrl } = useConfig()
  const { requestTicketHistory } = useSocketContext()
  const operatorName = useChatStore(s => s.operatorName)
  const setActiveTab = useChatStore(s => s.setActiveTab)
  const userName = usePersistStore(s => s.user?.userName)
  const t = useTranslations('message')
  const tCommon = useTranslations('common')
  const tTickets = useTranslations('tickets')

  const openTicket = (ticketId: string) => {
    setActiveTab('tickets')
    requestTicketHistory(ticketId)
  }

  const isUser = message?.role === 'user' && !message?.operatorId
  const isOperator = message?.role === 'user' && !!message?.operatorId
  const isAssistant = message?.role === 'assistant'
  const operatorFileImageUrl = useMemo(() => {
    if (!isOperator || !isContentString || !fileServiceUrl) return null

    return extractUrls(message.content as string).find(url => isFromFileService(url, fileServiceUrl)) ?? null
  }, [fileServiceUrl, isContentString, isOperator, message.content])

  const getRoleLabel = () => {
    if (isUser) return userName && userName.trim() && !userName.includes('null') ? userName : t('you')
    if (isOperator) return message?.operatorName || operatorName || tCommon('operator')

    return tCommon('operator')
  }

  const getRoleIcon = () => {
    if (isOperator) return <Headset className='size-3' />
    if (isAssistant) return <Headset className='size-3' />

    return null
  }

  const getMessageBubbleClasses = () => {
    const baseClasses = 'px-3.5 py-2.5 rounded-2xl overflow-hidden border border-current/20 shadow-sm'

    if (isUser) {
      return cn(baseClasses, 'bg-[var(--widget-visitor-bubble)] text-[var(--widget-visitor-text)] rounded-tr-sm')
    }

    switch (message?.type) {
      case 'error':
        return cn(baseClasses, 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-tl-none')
      case 'info':
        return cn(baseClasses, 'bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-200 rounded-tl-none')
      case 'form_submission':
        return cn(baseClasses, 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 rounded-tl-none')
      default:
        return cn(baseClasses, 'bg-[var(--widget-operator-bubble)] text-[var(--widget-operator-text)] rounded-tl-sm')
    }
  }

  const renderMessageContent = () => {
    if (message?.isThinking) {
      return (
        <div className='flex items-center gap-1 py-1' aria-label='typing' role='status'>
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className='bg-muted-foreground/60 animate-thinking-bounce inline-block size-1.5 rounded-full'
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </div>
      )
    }

    if (message?.type === 'show_form') {
      return <DynamicFormGenerator data={message?.content as any} />
    } else if (message?.type === 'component') {
      return <CustomComponentRenderer content={message?.content} />
    } else if (message?.type === 'article') {
      return <ArticleRenderer article={message?.content as any} />
    } else {
      if (operatorFileImageUrl) {
        return <OperatorFileAttachment src={operatorFileImageUrl} />
      }

      if (isContentString) {
        const visibleContent = stripInternalFileUploadedMarker(
          (message?.content as string)?.replace(/\[Resim.*?\]\n?/, '') ?? ''
        )
        if (!visibleContent) return null

        return <MarkdownMessage className='text-[13px] leading-relaxed font-normal' content={visibleContent} />
      }

      return null
    }
  }

  if (isContentString && isInternalFileUploadedMarker(message.content as string)) {
    return null
  }

  if (message?.type === 'notification') {
    return (
      <div ref={isLastMessage ? ref : null} className='flex items-center gap-3 px-2 py-1'>
        <div className='bg-border/50 h-px flex-1 rounded-full' />
        <span className='text-muted-foreground/60 max-w-xs flex-shrink-0 text-center text-xs leading-snug'>
          {message.content as string}
        </span>
        <div className='bg-border/50 h-px flex-1' />
      </div>
    )
  }

  if (message?.type === 'show_form') {
    const form = message.content as any
    if (form?.submitted_fields_redacted === true) return null
    if (!Array.isArray(form?.fields) || form.fields.length === 0) return null
  }

  if (message?.type === 'article' && !hasRenderableArticleContent(message.content as any)) return null

  const roleIcon = getRoleIcon()

  return (
    <div
      ref={isLastMessage ? ref : null}
      className={cn('flex items-start gap-2.5', isUser ? 'justify-end' : 'justify-start')}
    >
      {!isUser && roleIcon && (
        <span
          aria-hidden='true'
          className='border-primary/20 bg-primary/10 text-primary mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border'
        >
          {roleIcon}
        </span>
      )}
      <div className={cn('min-w-0', isUser ? 'max-w-[78%]' : 'max-w-[calc(100%-2.375rem)]')}>
        <span className='sr-only'>{getRoleLabel()}</span>
        <div className={getMessageBubbleClasses()}>
          {/* If it's an image placeholder, show the image */}
          {message?.type === 'file_placeholder' && message?.fileUrls && message?.fileUrls.length > 0 && (
            <div className='mb-2 grid grid-cols-4 gap-2 sm:grid-cols-6'>
              {message?.fileUrls.map((fileItem, i) => (
                <div key={i} className='aspect-square size-full overflow-hidden rounded'>
                  {fileItem.blobType.startsWith('video/') ? (
                    <video
                      src={fileItem.blobUrl}
                      controls
                      playsInline
                      preload='metadata'
                      className='size-full object-contain'
                    />
                  ) : (
                    <FilePreview
                      fileId={fileItem.currentFileId}
                      blobUrl={fileItem.blobUrl}
                      blobType={fileItem.blobType}
                      name={message?.fileName || t('uploadedImage')}
                      className='size-full cursor-pointer'
                    >
                      {fileItem.blobType.includes('application/pdf') ? (
                        <div className='flex aspect-square size-full flex-col items-center justify-center gap-1 bg-gray-300 p-4 text-gray-500 dark:bg-gray-600 dark:text-gray-400'>
                          <FileText className='w-24' />
                          <b className='text-xs'>PDF</b>
                        </div>
                      ) : (
                        <img
                          src={fileItem.blobUrl}
                          alt={message?.fileName || t('uploadedImage')}
                          className='size-full object-cover'
                        />
                      )}
                    </FilePreview>
                  )}
                </div>
              ))}
            </div>
          )}

          {renderMessageContent()}

          {message?.ticketId && !isUser && (
            <button
              onClick={() => openTicket(message.ticketId!)}
              className='border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 mt-2 flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all active:scale-95'
            >
              <TicketIcon className='size-3.5' />
              {tTickets('viewTicket')}
            </button>
          )}
        </div>
        {message.timestamp && (
          <div className={cn('text-muted-foreground mt-1 px-1 text-[11px]', isUser && 'text-end')}>
            <time dateTime={new Date(message.timestamp).toISOString()}>{formatTime(message.timestamp)}</time>
          </div>
        )}
      </div>
    </div>
  )
})

MessageItem.displayName = 'MessageItem'

export default MessageItem
